import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { componentSwitcher } from "../components/modules/componentSwitcher";
import { getToken, deleteToken } from "../services/cookies";
import { getCurrentPackageUser, getUUID } from "../services/api";
import ModuleList from "./dashboard/moduleList";
import Footer from "../components/footer";
import Notiflix from "notiflix";

const Captain = ({ children, noFooter = false }) => {
  const { id, moduleId } = useParams();
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  const token = getToken("jodiactoken");

  // Paket yönlendirme mapping'leri
  const PACKAGE_ROUTES = {
    11: "/11", // Yeni hesap - her zaman yönlendir
  };

  const NO_TOKEN_ROUTES = {
    12: "/new-capsule", // Zaman kapsülü - token yoksa yönlendir
    17: "/new-year-wishes", // Yeni yıl dilekleri - token yoksa yönlendir
  };

  // Ana sayfa için veri çekme
  const fetchUserData = async () => {
    try {
      const data = await getCurrentPackageUser(id);

      // Token varsa UUID kontrolü yap
      if (token) {
        const uuidResponse = await getUUID();
        const tokenuuid = uuidResponse?.uuid || uuidResponse;
        if (tokenuuid != id) {
          // Farklı kullanıcı ile giriş yapılmış, popup göster
          Notiflix.Confirm.show(
            'Farklı Hesap Tespit Edildi',
            'Başka bir hesap ile giriş yapmışsınız. Ne yapmak istersiniz?',
            'Çıkış Yap',
            'Devam Et',
            function okCb() {
              // Çıkış yap butonuna basıldı
              deleteToken("jodiactoken");

              // Eğer current_package 3 değilse ve NO_TOKEN_ROUTES'da varsa oraya yönlendir
              if (data.current_package !== 3 && NO_TOKEN_ROUTES[data.current_package]) {
                navigate("/" + id + NO_TOKEN_ROUTES[data.current_package]);
              } else {
                navigate("/" + id + "/login");
              }
            },
            function cancelCb() {
              // Devam et butonuna basıldı - mevcut kullanıcıya yönlendir
              navigate("/" + tokenuuid + "/");
            },
            {
              width: '320px',
              borderRadius: '8px',
              titleColor: '#3DA7B5',
              okButtonBackground: '#ef4444',
              okButtonColor: '#fff',
              cancelButtonBackground: '#3DA7B5',
              cancelButtonColor: '#fff',
            },
          );
          return;
        }
      }

      // Her zaman yönlendirilmesi gereken paketler
      if (PACKAGE_ROUTES[data.current_package]) {
        navigate("/" + id + PACKAGE_ROUTES[data.current_package]);
        return;
      }

      // Token yoksa yönlendirilmesi gereken paketler
      if (!token && NO_TOKEN_ROUTES[data.current_package]) {
        navigate("/" + id + NO_TOKEN_ROUTES[data.current_package]);
        return;
      }

      // Token kontrolü
      if (!token) {
        navigate("/" + id + "/login");
        return;
      }

      setReady(true);
    } catch (error) {
      navigate("/");
    }
  };

  useEffect(() => {
    fetchUserData();
    setReady(true);
  }, [id, moduleId]);

  if (!ready && !moduleId) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#e8e9ed]">
        <div className="flex flex-col items-center justify-center">
          {/* Modern Loading Animation */}
          <div className="relative mb-8">
            {/* Outer rotating ring */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            {/* Middle rotating ring (counter-clockwise) */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            {/* Inner pulsing circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
            {/* Center dot with ping effect */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-white animate-ping shadow-lg"></div>
            </div>
          </div>

          {/* Loading text with smooth animation */}
          <div className="space-y-3">
            <p className="text-xl font-semibold text-gray-800 animate-pulse">
              Yükleniyor...
            </p>
            <div className="flex justify-center gap-1.5">
              <span
                className="w-2.5 h-2.5 bg-[#3DA7B5] rounded-full animate-bounce shadow-md"
                style={{ animationDelay: "0ms", animationDuration: "1.4s" }}
              ></span>
              <span
                className="w-2.5 h-2.5 bg-[#3DA7B5] rounded-full animate-bounce shadow-md"
                style={{ animationDelay: "200ms", animationDuration: "1.4s" }}
              ></span>
              <span
                className="w-2.5 h-2.5 bg-[#3DA7B5] rounded-full animate-bounce shadow-md"
                style={{ animationDelay: "400ms", animationDuration: "1.4s" }}
              ></span>
            </div>
            {/* Progress bar effect */}
            <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden mt-4">
              <div
                className="h-full bg-gradient-to-r from-[#3DA7B5] to-[#2a8a96] rounded-full animate-pulse"
                style={{ width: "60%" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modül sayfası render - modül kendi verisini çekecek
  if (moduleId) {
    if (!token) {
      navigate("/" + id + "/login");
      return null;
    }
    const Component = componentSwitcher[moduleId]?.component;

    if (!Component) {
      return null;
    }

    return (
      <div className="flex justify-center w-full bg-[#e8e9ed]">
        <div className="w-full flex flex-col max-w-screen-sm">
          {Component({ id, moduleId: parseInt(moduleId) })}
        </div>
      </div>
    );
  }

  // Eğer children varsa (Settings, Profile, vb.), onları render et
  if (children) {
    return (
      <div className="flex justify-center w-full bg-[#e8e9ed]">
        <div className="w-full flex flex-col max-w-screen-sm">
          {children}
        </div>
      </div>
    );
  }

  // Ana sayfa (modül listesi) render
  return <ModuleList />;
};

export default Captain;
